import React, { useEffect, useRef, useState } from 'react';
import {
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { KnowledgeBaseEntry } from '../../../data/admin-knowledge-base';

interface InfoPopoverProps {
  entry: KnowledgeBaseEntry;
}

/**
 * Click-to-open info popover for operations-team explanations of section
 * features. Closes on outside-click or Escape; focus returns to the trigger.
 *
 * Anchored to its trigger via simple absolute positioning — no Floating UI
 * dependency. The panel is capped at viewport width so it never clips.
 */
export default function InfoPopover({ entry }: InfoPopoverProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    const onClick = (e: MouseEvent) => {
      const panel = popoverRef.current;
      const trigger = triggerRef.current;
      if (!panel || !trigger) return;
      const target = e.target as Node;
      if (!panel.contains(target) && !trigger.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div className="admin-info-popover">
      <button
        ref={triggerRef}
        type="button"
        className="admin-info-popover__trigger"
        aria-label={`About: ${entry.title}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <InformationCircleIcon
          className="admin-info-popover__icon"
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={entry.title}
          className="admin-info-popover__panel"
        >
          <header className="admin-info-popover__header">
            <h4 className="admin-info-popover__title">{entry.title}</h4>
            <button
              type="button"
              className="admin-info-popover__close"
              aria-label="Close"
              onClick={() => {
                setOpen(false);
                triggerRef.current?.focus();
              }}
            >
              <XMarkIcon
                className="admin-info-popover__close-icon"
                aria-hidden="true"
              />
            </button>
          </header>
          <p className="admin-info-popover__summary">{entry.summary}</p>
          {entry.features && entry.features.length > 0 && (
            <ul className="admin-info-popover__features">
              {entry.features.map((f) => (
                <li
                  key={f.name}
                  className="admin-info-popover__feature"
                >
                  <span className="admin-info-popover__feature-name">
                    {f.name}
                  </span>
                  <span className="admin-info-popover__feature-description">
                    {f.description}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
